import React from 'react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { CheckCircle, Circle, Loader, XCircle } from 'lucide-react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { SCHEDULE_STATUS } from '@/utils/schedule';
import AlertSection from '../../alert/AlertSection';
import { DATE_FORMAT, getTimestampUTC } from '@/utils/date';
import useMixPanel from '@/hooks/useMixPanel';
import { TOrganizationFAQ } from '@/services/api/organization-api';
import { availableDateType } from '@/services/api/services-api';

export type Schedule = {
  date: Date;
  slots: {
    from: string;
    to: string;
    status: string;
    statusDetail?: string;
  }[];
};

export type Props = {
  form: UseFormReturn<any>;
  organization: TOrganizationFAQ;
  availableDates?: availableDateType[];
  toMonth: Date;
  schedule: Schedule[];
  dateValue?: Date;
  timeIsLoading: boolean;
  hasSchedule: boolean;
  hasUnavailableSchedule?: boolean;
  continueRef: React.RefObject<HTMLButtonElement>;
  handleDateChange: () => void;
  handleOnMonthChange: (date: Date) => void;
  defaultDate: () => Date | undefined;
  findSchedule: (date: Date) => Schedule | undefined;
  scrollToContinue: (ref: React.RefObject<HTMLButtonElement>) => void;
  loadStepServicesData: (params: {
    firstReservationStartTime?: number;
  }) => void;
};

export const BookingByCalendar: React.FC<Props> = ({
  form,
  organization,
  availableDates,
  toMonth,
  schedule,
  dateValue,
  timeIsLoading,
  hasSchedule,
  hasUnavailableSchedule,
  continueRef,
  handleDateChange,
  handleOnMonthChange,
  defaultDate,
  findSchedule,
  scrollToContinue,
  loadStepServicesData
}) => {
  const { t } = useTranslation(['newBooking', 'common']);
  const Mixpanel = useMixPanel();

  return (
    <div className="my-8 flex flex-col gap-4">
      <FormField
        control={form.control}
        name="date"
        render={({ field }) => {
          return (
            <FormItem>
              <FormControl className="max-w-lg my-0 mx-auto">
                <Calendar
                  classNames={{
                    row: 'flex w-full mt-2 justify-around',
                    head_row: 'flex justify-around',
                    months: 'flex flex-col space-y-4 sm:space-x-4 sm:space-y-0'
                  }}
                  mode="single"
                  selected={field.value}
                  onSelect={(e) => {
                    if (e) {
                      Mixpanel.track('select date', {
                        screen: 'New Booking',
                        dateSelected: format(e, DATE_FORMAT)
                      });
                      form.reset({
                        ...form.getValues(),
                        date: e,
                        time: undefined
                      });
                      handleDateChange();
                    }
                  }}
                  fromMonth={new Date()}
                  toMonth={toMonth}
                  onMonthChange={(date) => handleOnMonthChange(date)}
                  defaultMonth={defaultDate()}
                  disabled={(date) => {
                    if (!availableDates) return true;
                    return !schedule.some(
                      (d) => date.getTime() === d.date.getTime()
                    );
                  }}
                  initialFocus
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          );
        }}
      />
      {timeIsLoading && !!form.getValues().date ? (
        <div>
          <FormLabel className="font-bold">{t('stepDateRadioTitle')}</FormLabel>
          <div className="flex items-center justify-center my-4 min-h-24">
            <Loader className="animate-spin" />
          </div>
        </div>
      ) : (
        <FormField
          control={form.control}
          name="time"
          render={({ field }) => {
            const slots = dateValue
              ? findSchedule(dateValue)?.slots
              : undefined;
            return slots ? (
              <FormItem>
                <FormLabel className="font-bold">
                  {t('stepDateRadioTitle')}
                  <p className="font-normal mt-2 mb-4">
                    {t('stepDateRadioDescripcion')}
                  </p>
                </FormLabel>
                <FormControl>
                  <FormItem className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 space-y-0 gap-3">
                    {slots?.map((slot) => {
                      const from = slot.from;
                      const to = slot.to;
                      const isSelected = field.value?.from === from;
                      const isAvailable =
                        slot.status === SCHEDULE_STATUS.available;
                      const value = `${from}-${to}`;
                      if (!isAvailable) {
                        return (
                          <Button
                            type="button"
                            key={value}
                            className="flex flex-col items-start h-20 border-red-400"
                            variant={'outline'}
                            disabled
                          >
                            <div className="flex">
                              <XCircle className="text-red-400" width={20} />
                              <p className="text-base ml-3">{value}</p>
                            </div>
                            <p className="text-red-600">
                              {slot.statusDetail
                                ? slot.statusDetail
                                : t('unAvailable')}
                            </p>
                          </Button>
                        );
                      }
                      return (
                        <Button
                          type="button"
                          key={value}
                          onClick={() => {
                            field.onChange(slot);
                            Mixpanel.track('select time', {
                              screen: 'New Booking',
                              timeSelected: value
                            });
                            const date = form.getValues().date;
                            const firstReservationStartTime = getTimestampUTC({
                              date,
                              time: slot.from,
                              tz: organization.tz
                            });
                            loadStepServicesData({
                              firstReservationStartTime
                            });
                            scrollToContinue(continueRef);
                          }}
                          variant={isSelected ? 'default' : 'outline'}
                          className="flex justify-start h-20"
                        >
                          <div>
                            {isSelected ? (
                              <CheckCircle width={20} />
                            ) : (
                              <Circle className="text-zinc-400" width={20} />
                            )}
                          </div>
                          <p className="text-base ml-3">{value}</p>
                        </Button>
                      );
                    })}
                  </FormItem>
                </FormControl>
                <FormMessage />
              </FormItem>
            ) : (
              <></>
            );
          }}
        />
      )}
      <div className="mb-8 space-y-2">
        {!hasSchedule && (
          <AlertSection variant="error" text={t('alertAvailablilityDates')} />
        )}
        {hasUnavailableSchedule && (
          <AlertSection variant="error" text={t('unAvailableMessage')} />
        )}
        <AlertSection variant="warning" text={t('messageAvailability')} />
        <AlertSection
          variant="info"
          text={t('messageTimezone', { tz: organization.tz })}
        />
      </div>
    </div>
  );
};
